/**
 * Official highlights: the NBA's own YouTube video for a game, embedded with the YouTube IFrame
 * API (never downloaded, never overlaid). Shown only when the server has GameMedia for the game.
 */
import { useEffect, useRef, useState } from 'react';
import type { GameMedia } from '@clutch/core';
import { useClient } from '../client';

export function useGameMedia(gameId: string | undefined): GameMedia | undefined {
  const client = useClient();
  const [media, setMedia] = useState<GameMedia>();
  useEffect(() => {
    if (!gameId) return;
    let alive = true;
    setMedia(undefined);
    client
      .media(gameId)
      .then((m) => alive && setMedia(m))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [client, gameId]);
  return media;
}

/** Clip start (seconds) for a play, if a person curated one. */
export const clipFor = (media: GameMedia | undefined, eventId: string | undefined) =>
  media && eventId ? media.clips.find((c) => c.eventId === eventId) : undefined;

type YTPlayer = { seekTo(s: number, allow: boolean): void; playVideo(): void; destroy(): void; getIframe?(): HTMLIFrameElement };
type YTNamespace = { Player: new (el: HTMLElement, o: Record<string, unknown>) => YTPlayer };
declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiPromise: Promise<YTNamespace | undefined> | undefined;
function loadYouTubeApi(timeoutMs = 6000): Promise<YTNamespace | undefined> {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  apiPromise ??= new Promise((resolve) => {
    const finish = (YT?: YTNamespace) => {
      clearTimeout(t);
      if (!YT) apiPromise = undefined; // A blocked first request must not disable retry forever.
      resolve(YT);
    };
    const t = setTimeout(() => finish(), timeoutMs);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      finish(window.YT);
    };
    const s = document.createElement('script');
    s.src = 'https://www.youtube.com/iframe_api';
    s.async = true;
    s.onerror = () => {
      finish();
    };
    document.head.appendChild(s);
  });
  return apiPromise;
}

function YouTubeEmbed({ videoId, start, title, onUnavailable }: { videoId: string; start?: number; title: string; onUnavailable: () => void }) {
  const host = useRef<HTMLDivElement>(null);
  const player = useRef<YTPlayer>();
  const unavailable = useRef(onUnavailable);
  unavailable.current = onUnavailable;
  const initialStart = useRef(start);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    let watchdog: ReturnType<typeof setTimeout>;
    setReady(false);
    const fail = () => { if (alive) unavailable.current(); };
    // Neither a blocked iframe nor a stalled player should leave a permanent black stage.
    watchdog = setTimeout(fail, 12_000);
    loadYouTubeApi().then((YT) => {
      if (!alive || !host.current) return;
      if (!YT) return fail();
      const el = document.createElement('div');
      host.current.appendChild(el);
      try {
        player.current = new YT.Player(el, {
          videoId, width: '100%', height: '100%',
          playerVars: { playsinline: 1, rel: 0, origin: window.location.origin, ...(initialStart.current ? { start: Math.floor(initialStart.current) } : {}) },
          events: {
            onReady: ({ target }: { target: YTPlayer }) => {
              if (!alive) return;
              clearTimeout(watchdog);
              target.getIframe?.().setAttribute('title', title);
              setReady(true);
              // This component mounts only after an explicit Watch action. Browsers may still
              // require a tap in YouTube's own player; its controls remain unobstructed.
              target.playVideo();
            },
            onStateChange: ({ data }: { data: number }) => {
              clearTimeout(watchdog);
              if (data === 3) watchdog = setTimeout(fail, 15_000);
            },
            onError: fail,
          },
        });
      } catch { fail(); }
    });
    return () => {
      alive = false;
      clearTimeout(watchdog);
      player.current?.destroy();
      player.current = undefined;
      host.current?.replaceChildren();
    };
  }, [videoId, title]); // start is applied by the effect below once the player exists

  useEffect(() => {
    if (start === undefined || !player.current) return;
    try {
      player.current.seekTo(start, true);
      player.current.playVideo();
    } catch {
      /* not ready yet: the initial start param covers it */
    }
  }, [start]);

  return <><div className={`yt__host${ready ? ' is-ready' : ''}`} ref={host} />{!ready && <div className="yt__loading" role="status"><span className="yt__loading-mark" aria-hidden="true">▶</span>Opening official highlights…</div>}</>;
}

/** Video is user-initiated and never presented as synchronized live coverage. */
export function OfficialVideo({ media }: { media: GameMedia }) {
  const [watching, setWatching] = useState(false);
  const [failed, setFailed] = useState(false);
  const play = () => { setFailed(false); setWatching(true); };
  useEffect(() => { setWatching(false); setFailed(false); }, [media.videoId]);
  return <section className="official-video" aria-label="Watch official video">
    <div className="official-video__heading"><span className="eyebrow">OFFICIAL NBA · HIGHLIGHTS</span>{watching ? <button className="official-video__back" onClick={() => setWatching(false)}>Back to court</button> : <a href={`https://www.youtube.com/watch?v=${media.videoId}`} target="_blank" rel="noopener noreferrer">YouTube ↗</a>}</div>
    {watching ? <div className="yt"><YouTubeEmbed videoId={media.videoId} title={media.title} onUnavailable={() => { setWatching(false); setFailed(true); }} /></div>
      : failed ? <div className="video-unavailable" role="status" data-testid="video-unavailable"><b>Stay with the game.</b><p>These highlights couldn’t play here. Follow every play below, or open the video on YouTube.</p><button onClick={play}>Try video again</button></div>
      : <button className="official-video__poster" onClick={play} aria-label="Play official NBA highlights">
        <img src={`https://i.ytimg.com/vi/${media.videoId}/hqdefault.jpg`} alt="" onError={(e) => { e.currentTarget.hidden = true; }} /><span>▶</span><b>Watch the finish</b>
      </button>}
    {!failed && <><p>{media.title}</p><small>Official highlights · independent of the play-by-play below.</small></>}
  </section>;
}

export function HighlightsSheet({ media, start, onClose }: { media: GameMedia; start?: number; onClose: () => void }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="sheet-scrim" onClick={onClose}>
      <section className="sheet highlights" role="dialog" aria-modal="true" aria-label="Official highlights" data-testid="highlights-sheet" onClick={(e) => e.stopPropagation()}>
        <span className="sheet__grab" aria-hidden="true" />
        <p className="highlights__eyebrow">
          <span className="highlights__yt" aria-hidden="true">▶</span> OFFICIAL VIDEO · YOUTUBE
        </p>
        <h2 className="sheet__title highlights__title">{media.title}</h2>
        {failed ? <div className="video-unavailable" role="status" data-testid="video-unavailable"><b>These highlights couldn’t play here.</b><p>You can keep following the court or open the official video on YouTube.</p><button onClick={() => setFailed(false)}>Try video again</button></div> : <div className="yt">
          <YouTubeEmbed videoId={media.videoId} title={media.title} {...(start !== undefined ? { start } : {})} onUnavailable={() => setFailed(true)} />
        </div>}
        <p className="highlights__note">Official highlights, embedded from YouTube. Not synced to the court — the court is the live view.</p>
        <a href={`https://www.youtube.com/watch?v=${media.videoId}${start ? `&t=${Math.floor(start)}s` : ''}`} target="_blank" rel="noopener noreferrer">Open on YouTube ↗</a>
        <button className="btn btn--ghost btn--block" onClick={onClose}>
          Back to the court
        </button>
      </section>
    </div>
  );
}
